import React from 'react';
import styled from 'styled-components';

import OrbitSendIcon from '../../icons/OrbitSendIcon';
import { orbitColorsFor } from '../../styles';
import useOrbitComposer from '../../useOrbitComposer';
import { ORBIT_PAGE_MOCK } from '../orbitPageMock';
import OrbitWorkspacePicker, { OrbitWorkspacePickerPlacement } from './OrbitWorkspacePicker';
import { pg, PlainButton, tint, type } from './pageStyles';

// `pg.link` is a `var()` reference with no channels for `tint`, so the rule and ring tints are taken
// from the dark reading the frame is drawn in.
const ACTION_HEX = orbitColorsFor('dark').action;

const Root = styled.div`
  width: 100%;
`;

/*
  Figma 6410:9287. Its 9.8 / 10.8 / 9.8 / 14.8 padding includes the 0.8 rule, which is what makes
  the box hug the 40px button at 59.6. The 3px ring is drawn on the empty box, so it is the resting
  state; focus strengthens the rule and the ring, which the file does not draw.
*/
const Box = styled.form`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 9px 10px 9px 14px;
  border: 0.8px solid ${tint(ACTION_HEX, 0.5)};
  border-radius: 15px;
  background: ${pg.card};
  box-shadow: 0 0 0 3px ${tint(ACTION_HEX, 0.06)};
  text-align: left;

  > * + * {
    margin-left: 10px;
  }

  &:focus-within {
    border-color: ${pg.link};
    box-shadow: 0 0 0 3px ${tint(ACTION_HEX, 0.25)};
  }
`;

const Input = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${pg.text};
  ${type('composer')}

  &::placeholder {
    color: ${pg.placeholder};
  }
`;

// 6410:9296: the gradient runs top to bottom. No disabled state is drawn.
const Send = styled(PlainButton)`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  background-image: linear-gradient(180deg, ${pg.link} 0%, #1b6fd1 100%);
  color: #ffffff;

  &:focus-visible {
    outline: 2px solid ${pg.link};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const AskError = styled.p`
  margin: 8px 0 0;
  color: ${pg.danger};
  ${type('containerBody')}
`;

interface IOrbitPageComposerProps {
  inputRef?: React.Ref<HTMLInputElement>;
  pickerPlacement: OrbitWorkspacePickerPlacement;
}

/** The full-page composer, the same box on the welcome view and under a chat. */
const OrbitPageComposer: React.FC<IOrbitPageComposerProps> = ({ inputRef, pickerPlacement }) => {
  const composer = useOrbitComposer();
  return (
    <Root data-testid="orbit-page-composer">
      <Box onSubmit={composer.handleSubmit} data-testid="orbit-page-composer-box">
        <Input
          ref={inputRef}
          aria-label="Ask Orbit"
          placeholder={ORBIT_PAGE_MOCK.welcome.placeholder}
          value={composer.draft}
          readOnly={composer.isAsking}
          onChange={composer.handleChange}
          onKeyDown={composer.handleKeyDown}
        />
        <OrbitWorkspacePicker placement={pickerPlacement} />
        <Send type="submit" aria-label="Send" disabled={!composer.canSubmit}>
          <OrbitSendIcon />
        </Send>
      </Box>
      {composer.askErrorText !== null ? (
        <AskError role="alert">{composer.askErrorText}</AskError>
      ) : null}
    </Root>
  );
};

export default OrbitPageComposer;
